import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'language/translator'
import { cloneDeep } from 'lodash'
import { nanoid } from 'nanoid'

import { getTheme } from 'themes'
import { ThemeProvider } from '@mui/material'

import type { TAttributeOptionDto } from '@homepal/api-contract'
import {
  FilterConditionFormatBody,
  FilterPatchBody
} from 'types/GlobalKpiOption'

import Button from 'components_new/atoms/Button'

import SimpleDialog from 'components_new/molecules/SimpleDialog'

import NativeFilterGroupSection from '../NativeFilterGroupSection'

import { formatUpdateFilterBody } from '../utils'

interface FilterGroupDialogProps {
  allFilters: FilterConditionFormatBody[]
  filterAttributeOptions: TAttributeOptionDto[]
  onClose: () => void
  open: boolean
  updateFilters: (filters: FilterPatchBody[]) => void
}

const FilterGroupDialog = (props: FilterGroupDialogProps) => {
  const { allFilters, filterAttributeOptions, onClose, open, updateFilters } =
    props
  const [filters, setFilters] = useState<FilterConditionFormatBody[]>([])
  const { t } = useTranslation()
  const groupId = useMemo(() => nanoid(), [open])

  useEffect(() => {
    setFilters([])
  }, [open])

  const baseFilter = useMemo(() => {
    const filter = cloneDeep(allFilters[0])

    filter.attribute_option.selected = null
    filter.condition.selected = null
    filter.type.selected = null

    return filter
  }, [allFilters])

  const handleClose = () => {
    onClose()
  }

  return (
    <ThemeProvider theme={getTheme('light')}>
      <SimpleDialog
        actions={
          <>
            <Button onClick={onClose} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button
              onClick={() => {
                const newFilters = formatUpdateFilterBody([
                  ...allFilters,
                  ...filters
                ])

                updateFilters(newFilters)
                onClose()
              }}
            >
              {t('global', 'save')}
            </Button>
          </>
        }
        contentText={t('filters', 'group_dialog_content')}
        onClose={handleClose}
        open={open}
        title={t('filters', 'add_filter_group')}
        sx={{ zIndex: 1302 }}
      >
        <NativeFilterGroupSection
          filterAttributeOptions={filterAttributeOptions}
          filterGroupId={groupId}
          filters={filters.length === 0 ? [baseFilter] : filters}
          updateFilters={(filters) => {
            const newFilters: { [id: string]: FilterConditionFormatBody } = {}

            filters.forEach((filter) => {
              const key = `${filter.attribute_option_id}_${filter.condition}_${filter.type}`

              if (key in newFilters) {
                (newFilters[key].value.selected as string[]).push(
                  filter.value as string
                )
              } else {
                newFilters[key] = {
                  filter_group_id: groupId,
                  attribute_option: {
                    selected: filter.attribute_option_id
                  },
                  condition: {
                    selected: filter.condition,
                    options: baseFilter.condition.options
                  },
                  type: {
                    selected: filter.type,
                    options: baseFilter.type.options
                  },
                  value: {
                    selected:
                      typeof filter.value === 'string'
                        ? [filter.value]
                        : filter.value
                  },
                  from_value: {
                    selected: filter.from_value || null
                  },
                  to_value: {
                    selected: filter.to_value || null
                  }
                }
              }
            })

            setFilters(Object.values(newFilters))
          }}
        />
      </SimpleDialog>
    </ThemeProvider>
  )
}

export default FilterGroupDialog
